import React, {ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'

type Props = {
 currentPage?: number
 itemsPerPage?: number
 totalRecord?: number
 onPageChange?: (pageNo: number) => void
 setItemsPerPage?: (pageSize: number) => void
}

const KTPagination = ({
 currentPage = 0,
 itemsPerPage = 10,
 totalRecord = 0,
 onPageChange,
 setItemsPerPage,
}: Props) => {
 const {t} = useTranslation()
 let startPage: number
 let endPage: number
 const totalPages = Math.ceil(totalRecord / itemsPerPage)
 if (totalPages <= 5) {
  // Less than 5 total pages, show all
  startPage = 1
  endPage = totalPages
 } else {
  // More than 5 total pages, calculate window
  if (currentPage <= 3) {
   startPage = 1
   endPage = 5
  } else if (currentPage + 2 >= totalPages) {
   startPage = totalPages - 4
   endPage = totalPages
  } else {
   startPage = currentPage - 2
   endPage = currentPage + 2
  }
 }
 const pagesToShow = Array.from(
  {length: endPage - startPage + 1},
  (_, i) => startPage + i
 )

 const handlePageSizeChange = (
  event: ChangeEvent<HTMLSelectElement>
 ) => {
  if (setItemsPerPage)
   setItemsPerPage(Number(event.target.value))
 }

 return (
  <nav className='flex justify-between items-center mt-4 text-base font-medium '>
   <div className='text-[#5A6066]'>
    {`${(currentPage - 1) * itemsPerPage + 1}-${
     totalRecord <
     (currentPage - 1) * itemsPerPage + itemsPerPage
      ? totalRecord
      : (currentPage - 1) * itemsPerPage + itemsPerPage
    } ${t('pagination.outOf')} ${totalRecord}`}
   </div>
   {setItemsPerPage && (
    <div className='text-[#5A6066] flex justify-center items-center'>
     <select
      className='px-2 py-1 relative rounded-sm bg-white focus-within:border-[#000000] rounded-lg border border-solid border-[#E6EBF0] bg-white focus-within:border-[#000000]'
      value={itemsPerPage}
      onChange={handlePageSizeChange}
     >
      {[10, 25, 50].map((size) => (
       <option key={size} value={size}>
        {size}
       </option>
      ))}
     </select>
     <span className='pl-2'>
      {t('pagination.enteriesPerPage')}
     </span>
    </div>
   )}
   {onPageChange && (
    <ul className='flex list-none p-0'>
     <li>
      <button
       type='button'
       onClick={() => onPageChange(currentPage - 1)}
       disabled={currentPage === 1}
       className='px-3 py-1 text-[#337AB8] disabled:opacity-50 text-base font-medium'
      >
       {t('pagination.pre')}
      </button>
     </li>
     {pagesToShow.map((number, key) => (
      <li key={number}>
       <button
        type='button'
        disabled={currentPage === number}
        onClick={() => onPageChange(number)}
        className={`${
         key < pagesToShow.length - 1 &&
         'border-r-2 border-[#CED8E0]'
        } px-3 py-1 text-[#337AB8] disabled:text-gray disabled:bg-white hover:bg-gray-100`}
       >
        {number}
       </button>
      </li>
     ))}
     {endPage < totalPages && (
      <li className='px-4 self-end pb-2'>
       <svg
        xmlns='http://www.w3.org/2000/svg'
        width='17'
        height='4'
        viewBox='0 0 17 4'
        fill='none'
       >
        <circle
         cx='1.5'
         cy='2.00391'
         r='1.5'
         fill='#5A6066'
        />
        <circle
         cx='8.5'
         cy='2.00391'
         r='1.5'
         fill='#5A6066'
        />
        <circle
         cx='15.5'
         cy='2.00391'
         r='1.5'
         fill='#5A6066'
        />
       </svg>
      </li>
     )}
     <li>
      <button
       type='button'
       onClick={() => onPageChange(currentPage + 1)}
       disabled={
        totalRecord === 0 || currentPage === totalPages
       }
       className='px-3 py-1 disabled:opacity-50 text-[#337AB8] disabled:text-gray ml-1'
      >
       {t('pagination.next')}
      </button>
     </li>
    </ul>
   )}
  </nav>
 )
}

export default KTPagination
